import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Trash2, TrendingUp, Info } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import { getHistory, clearHistory } from "../lib/history";
import { fmtCurrency, fmtPct, fmtRatio } from "../lib/format";
import SiteFootnote from "../components/SiteFootnote";

const COLORS = {
  primary: "#1F382E",
  accent: "#C26D5C",
  sand: "#D4C9B6",
  border: "#E5E0D8",
  muted: "#5C665D",
  success: "#2C5C43",
};

export default function TrendsPage() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(getHistory());
  }, []);

  // Chronological order — assume saved_at already represents period order
  const ordered = useMemo(
    () => [...items].sort((a, b) => new Date(a.saved_at) - new Date(b.saved_at)),
    [items]
  );

  const data = useMemo(
    () =>
      ordered.map((it, i) => ({
        period: it.period || `Period ${i + 1}`,
        score: it.score,
        revenue: it.metrics.revenue,
        ebitda_margin: it.metrics.ebitda_margin,
        net_margin: it.metrics.net_margin,
        dscr: it.metrics.dscr,
        current_ratio: it.metrics.current_ratio,
        debt_to_ebitda: it.metrics.debt_to_ebitda,
      })),
    [ordered]
  );

  function handleClear() {
    if (window.confirm("Clear all saved periods?")) {
      clearHistory();
      setItems([]);
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative">
      <header className="max-w-7xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <BrandWordmark size="md" />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/analyze">
            <Button variant="ghost" className="text-[#5C665D] hover:bg-[#D4C9B6]/40">
              <ArrowLeft size={14} strokeWidth={1.5} className="mr-2" />
              New analysis
            </Button>
          </Link>
          {items.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClear}
              className="border-[#E5E0D8] text-[#A84632] hover:bg-[#A84632] hover:text-white transition-all duration-300"
              data-testid="clear-history-btn"
            >
              <Trash2 size={14} strokeWidth={1.5} className="mr-2" />
              Clear
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">Trends</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-tight mt-3 tracking-tight">
          The story your numbers tell over time.
        </h1>
        <p className="text-[#5C665D] leading-relaxed mt-3 max-w-2xl">
          Every analysis you run is saved here — up to the last eight periods — so you can spot
          the trajectory, not just the snapshot.
        </p>

        {items.length === 0 ? (
          <EmptyState />
        ) : items.length === 1 ? (
          <SinglePeriod item={items[0]} />
        ) : (
          <div className="mt-10 space-y-6">
            <ChartCard
              title="CFO Grade"
              subtitle="0–100 composite of profitability, liquidity, solvency and efficiency."
              data={data}
              dataKey="score"
              suffix=""
              color={COLORS.primary}
              testid="trend-chart-score"
              learnSlug="letter-grade"
            />
            <div className="grid md:grid-cols-2 gap-6">
              <ChartCard
                title="Revenue"
                subtitle="Top-line over time."
                data={data}
                dataKey="revenue"
                isCurrency
                color={COLORS.primary}
              />
              <ChartCard
                title="EBITDA margin"
                subtitle="Cash earning power."
                data={data}
                dataKey="ebitda_margin"
                suffix="%"
                color={COLORS.accent}
                learnSlug="ebitda-margin"
              />
              <ChartCard
                title="DSCR"
                subtitle="Debt service coverage. Lender benchmark: 1.25x."
                data={data}
                dataKey="dscr"
                suffix="x"
                color={COLORS.success}
                learnSlug="dscr"
              />
              <ChartCard
                title="Debt / EBITDA"
                subtitle="Years of cash earnings to clear debt — lower is better."
                data={data}
                dataKey="debt_to_ebitda"
                suffix="x"
                color={COLORS.accent}
                learnSlug="debt-to-ebitda"
              />
            </div>

            <PeriodTable items={ordered} />
          </div>
        )}
      </main>
      <SiteFootnote />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mt-12 bg-white border border-[#E5E0D8] rounded-2xl p-12 text-center">
      <TrendingUp size={28} strokeWidth={1.5} className="mx-auto text-[#C26D5C]" />
      <h3 className="font-serif-display text-2xl text-[#1F382E] mt-4">
        No periods saved yet.
      </h3>
      <p className="text-[#5C665D] mt-2 max-w-md mx-auto leading-relaxed">
        Run an analysis and click <strong>Save & compare</strong> on the report to start a trend.
      </p>
      <Link to="/analyze">
        <Button className="mt-6 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md transition-all duration-300">
          Run an analysis
        </Button>
      </Link>
    </div>
  );
}

function SinglePeriod({ item }) {
  return (
    <div className="mt-12 bg-white border border-[#E5E0D8] rounded-2xl p-10 max-w-2xl">
      <p className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">First period saved</p>
      <h3 className="font-serif-display text-3xl text-[#1F382E] mt-2">{item.company}</h3>
      <p className="text-[#5C665D] mt-1">
        {item.period} · {item.industry}
      </p>
      <p className="font-serif-display text-5xl text-[#1F382E] mt-6 tabular-nums leading-none">
        {Math.round(item.score)}
        <span className="text-base text-[#5C665D] font-sans"> / 100</span>
      </p>
      <p className="text-[#5C665D] mt-6 leading-relaxed">
        Save another period to start seeing trend lines here. The story gets interesting around
        period three.
      </p>
    </div>
  );
}

function ChartCard({ title, subtitle, data, dataKey, color, suffix = "", isCurrency = false, testid, learnSlug }) {
  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none" data-testid={testid}>
      <CardHeader>
        <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D] flex items-center gap-1.5">
          <span>{title}</span>
          {learnSlug && (
            <Link
              to={`/learn/${learnSlug}`}
              className="text-[#5C665D] hover:text-[#1F382E] transition-colors"
              aria-label={`Learn more about ${title}`}
              data-testid={`trends-learn-${learnSlug}`}
            >
              <Info size={11} strokeWidth={1.75} />
            </Link>
          )}
        </CardDescription>
        <CardTitle className="font-serif-display text-xl text-[#1F382E]">
          {subtitle}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={COLORS.border} vertical={false} />
              <XAxis
                dataKey="period"
                stroke={COLORS.muted}
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke={COLORS.muted}
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  isCurrency ? `$${(v / 1000).toFixed(0)}k` : `${Number(v).toFixed(suffix === "x" ? 1 : 0)}${suffix}`
                }
              />
              <Tooltip
                cursor={{ stroke: COLORS.border }}
                contentStyle={{
                  background: "#fff",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 8,
                }}
                formatter={(v) =>
                  isCurrency
                    ? fmtCurrency(v)
                    : suffix === "%"
                      ? fmtPct(v)
                      : suffix === "x"
                        ? fmtRatio(v)
                        : Number(v).toFixed(0)
                }
              />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={2.5}
                dot={{ r: 4, fill: color, strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function PeriodTable({ items }) {
  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none">
      <CardHeader>
        <CardTitle className="font-serif-display text-xl text-[#1F382E]">
          Periods on file
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#E5E0D8] text-[#5C665D]">
                <th className="py-2.5 pr-4 font-medium">Period</th>
                <th className="py-2.5 pr-4 font-medium">Company</th>
                <th className="py-2.5 pr-4 font-medium text-right">Score</th>
                <th className="py-2.5 pr-4 font-medium text-right">Revenue</th>
                <th className="py-2.5 pr-4 font-medium text-right">EBITDA %</th>
                <th className="py-2.5 pr-4 font-medium text-right">DSCR</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#F0EDE5] last:border-b-0">
                  <td className="py-2.5 pr-4 text-[#1A1A1A]">{it.period}</td>
                  <td className="py-2.5 pr-4 text-[#1A1A1A]">{it.company}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums text-[#1F382E]">
                    {Math.round(it.score)}
                  </td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">
                    {fmtCurrency(it.metrics.revenue)}
                  </td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">
                    {fmtPct(it.metrics.ebitda_margin)}
                  </td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">
                    {fmtRatio(it.metrics.dscr)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
